import { useState, useEffect } from "react";
import { PatientDetails } from "./PatientDetails";
import { Questionnaires } from "./Questionaries";
import { Treatments } from "./Treatments";
import instance from "../../../../../../axios/instance";

const dummyPatientDetails = {
  name: "John Doe",
  dob: "1990-05-15",
  mobile: "+1 (555) 123-4567",
  email: "example@gmail.com",
  weight: "180 lbs",
  height: "6' 0\"",
  paymentMethod: "**** **** **** 1234",
  cardName: "John Doe",
  expiresOn: "12/26",
  country: "USA",
  state: "California",
  city: "Los Angeles",
  houseAddr: "123 block A",
  zipCode: "12345",
};

const TabButton = ({ label, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 text-sm font-medium ${
      isActive
        ? "border-primary border-b-2 text-black"
        : "text-[#717179]  hover:text-black"
    }`}
  >
    {label}
  </button>
);

export const CMSSheetContent = ({
  selectedRow,
  onRefresh,
  triggerEditModal,
  onGetEditHandlers,
  onTabChange,
  onTreatmentViewChange,
  initialTab = "patientDetails",
}) => {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [patientDetails, setPatientDetails] = useState(null);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [isEditingQuestionnaire, setIsEditingQuestionnaire] = useState(false);
  const [questionnaireHandlers, setQuestionnaireHandlers] = useState(null);
  const token = window.sessionStorage.getItem("ACCESS_TOKEN");

  useEffect(() => {
    setActiveTab(initialTab);
    setIsEditingQuestionnaire(false);
  }, [initialTab, selectedRow]);

  useEffect(() => {
    setIsEditingQuestionnaire(false);
    if (onTabChange) {
      onTabChange(activeTab);
    }
  }, [activeTab]);

  useEffect(() => {
    if (triggerEditModal > 0) {
      if (activeTab === "patientDetails") {
        setOpenEditModal(true);
        setTimeout(() => setOpenEditModal(false), 100);
      } else if (activeTab === "questionnaires") {
        setIsEditingQuestionnaire(true);
      }
    }
  }, [triggerEditModal]);

  useEffect(() => {
    if (onGetEditHandlers) {
      if (activeTab === "questionnaires" && isEditingQuestionnaire) {
        onGetEditHandlers({
          type: "questionnaire",
          handlers: questionnaireHandlers,
        });
      } else {
        onGetEditHandlers(null);
      }
    }
  }, [
    activeTab,
    isEditingQuestionnaire,
    questionnaireHandlers,
    onGetEditHandlers,
  ]);

  useEffect(() => {
    if (selectedRow) {
      fetchPatientData();
      fetchQuestionnaireData();
    }
  }, [selectedRow]);

  const fetchPatientData = async () => {
    if (!selectedRow?.patientId) {
      setPatientDetails({
        name: selectedRow?.name || "N/A",
        email: selectedRow?.email || "N/A",
        dob: "Not registered yet",
        mobile: "Not registered yet",
        weight: "Not registered yet",
        height: "Not registered yet",
        paymentMethod: "Not registered yet",
        cardName: "Not registered yet",
        expiresOn: "N/A",
        country: "Not registered yet",
        state: "Not registered yet",
        city: "Not registered yet",
        houseAddr: "Not registered yet",
        zipCode: "Not registered yet",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await instance.post(
        "/doctor/patient/single",
        { patientId: selectedRow.patientId },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const patient = response.data;

      let paymentMethod = "Not set";
      let cardName = "Not set";
      try {
        const paymentResponse = await instance.get(
          `/doctor/patient/${selectedRow.patientId}/payment-info`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (paymentResponse.data && paymentResponse.data.hasPaymentMethod) {
          paymentMethod = "•••• •••• •••• ••••";
          cardName =
            paymentResponse.data.cardName ||
            `${patient.firstname || ""} ${patient.lastname || ""}`.trim();
        }
      } catch (err) {}

      setPatientDetails({
        name:
          `${patient.firstname || ""} ${patient.lastname || ""}`.trim() ||
          selectedRow.name,
        dob: patient.dob || "N/A",
        mobile: patient.cell_phone || patient.phone || "N/A",
        email: patient.email || selectedRow.email || "N/A",
        weight: patient.weight ? `${patient.weight} lbs` : "N/A",
        height: patient.height || "N/A",
        paymentMethod: paymentMethod,
        cardName: cardName,
        expiresOn: "N/A",
        country: patient.country || "USA",
        state: patient.state || "N/A",
        city: patient.city || "N/A",
        houseAddr: patient.address1 || "N/A",
        zipCode: patient.zipcode || "N/A",
      });
    } catch (error) {
      console.error("Error fetching patient data:", error);
      setPatientDetails({
        name: selectedRow.name,
        email: selectedRow.email,
        dob: "N/A",
        mobile: "N/A",
        weight: "N/A",
        height: "N/A",
        paymentMethod: "N/A",
        cardName: selectedRow.name,
        expiresOn: "N/A",
        country: "N/A",
        state: "N/A",
        city: "N/A",
        houseAddr: "N/A",
        zipCode: "N/A",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestionnaireData = async () => {
    if (!selectedRow?.questionnaireId) {
      setQuestionnaires([]);
      return;
    }

    setLoading(true);
    try {
      const response = await instance.get(
        `/doctor/questionnaire/${selectedRow.questionnaireId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const flat = response.data.questionnaires;
      setQuestionnaires(Array.isArray(flat) ? flat : []);
    } catch (error) {
      console.error("Error fetching questionnaire data:", error);
      setQuestionnaires([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="border-b border-gray-200">
        <div className="flex items-center gap-2">
          <TabButton
            label="Patient Details"
            isActive={activeTab === "patientDetails"}
            onClick={() => setActiveTab("patientDetails")}
          />
          <TabButton
            label="Questionnaires"
            isActive={activeTab === "questionnaires"}
            onClick={() => setActiveTab("questionnaires")}
          />
          <TabButton
            label="Treatments"
            isActive={activeTab === "treatments"}
            onClick={() => setActiveTab("treatments")}
          />
        </div>
      </div>

      <div>
        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading...</div>
        ) : (
          <>
            {activeTab === "patientDetails" && (
              <PatientDetails
                details={patientDetails || dummyPatientDetails}
                patientId={selectedRow?.patientId}
                openEditModal={openEditModal}
                onRefresh={() => {
                  fetchPatientData();
                  if (onRefresh) onRefresh();
                }}
              />
            )}
            {activeTab === "questionnaires" && (
              <Questionnaires
                questionnaires={questionnaires}
                questionnaireId={selectedRow?.questionnaireId}
                onRefresh={() => {
                  fetchQuestionnaireData();
                  if (onRefresh) onRefresh();
                }}
                onEditClick={(handlers) => setQuestionnaireHandlers(handlers)}
                isEditing={isEditingQuestionnaire}
                onCancelEdit={() => setIsEditingQuestionnaire(false)}
              />
            )}
            {activeTab === "treatments" && (
              <Treatments
                selectedRow={selectedRow}
                triggerEditModal={triggerEditModal}
                onViewChange={onTreatmentViewChange}
                onRefresh={onRefresh}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};
